/**
 * Hook: PUT /api/t/{slug}/candidates/{id} — status update mutation.
 *
 * Note: spec L3640 names PATCH but the registered handler is `@router.put`
 * (engineer brief §"Surprises you should expect"). We use PUT to match the
 * actual server contract.
 *
 * On success: invalidates the whole ``['candidates', tenantSlug]`` prefix,
 * the single-candidate row, AND the dashboard ``['jd-candidates', tenantSlug]``
 * card list (the candidate's status badge may be stale across any JD it is
 * paired to). (Architect §4 invalidation cascade.)
 *
 * `refetchType: "all"` on the list invalidations is load-bearing
 * (fix/stale-candidate-card-after-delete, status-mutation arm). This hook is
 * triggered from `<CandidatesTableRow>` (the candidates list page) and from
 * `<CandidatePageHeader>` — never from `<Dashboard>` itself. The dashboard's
 * `["jd-candidates", …]` / `["candidates", …]` queries are therefore INACTIVE
 * when the status change fires. With the default `refetchType: "active"`,
 * `invalidateQueries` marks those queries stale but never refetches them, so
 * a status change made while the dashboard is unmounted leaves a stale
 * pipeline card (same root cause as the deleted-card bug). `"all"` forces the
 * inactive queries to refetch immediately, so the cache is correct before
 * `<Dashboard>` ever remounts.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Candidate } from "./useCandidates";
import type { CandidateStatus } from "@/components/candidates/StatusPill";

interface UpdateStatusVariables {
  id: string;
  status: CandidateStatus;
}

export function useUpdateCandidateStatus(): UseMutationResult<
  Candidate,
  ApiError,
  UpdateStatusVariables
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const qc = useQueryClient();

  return useMutation<Candidate, ApiError, UpdateStatusVariables>({
    mutationFn: async ({ id, status }) => {
      const r = await apiClient.put<Candidate>(
        `/api/t/${tenantSlug}/candidates/${id}`,
        { status },
      );
      return r.data;
    },
    onSuccess: (_data, variables) => {
      // Candidates list — `refetchType: "all"` so the list refetches even
      // when triggered from a surface where `<Candidates>` is unmounted
      // (inactive-query staleness; see header docstring).
      void qc.invalidateQueries({
        queryKey: ["candidates", tenantSlug],
        refetchType: "all",
      });
      void qc.invalidateQueries({
        queryKey: ["candidate", tenantSlug, variables.id],
      });
      // Dashboard candidate cards: the status badge may be stale on any JD
      // this candidate is paired to. Invalidate the whole prefix.
      // `refetchType: "all"` is mandatory — the dashboard is unmounted while
      // this mutation fires (it runs from the candidates list / candidate
      // page), so its `["jd-candidates", …]` query is INACTIVE and the
      // default `"active"` would leave it stale. See the header docstring.
      void qc.invalidateQueries({
        queryKey: ["jd-candidates", tenantSlug],
        refetchType: "all",
      });
    },
  });
}
